import {
  Component,
  Input,
  OnInit,
  TemplateRef,
  Type,
  ViewChild,
} from '@angular/core';
import {DatePipe} from '@angular/common';
import {FormBuilder, Validators} from '@angular/forms';
import {TuiAlertService, TuiDialogService} from '@taiga-ui/core';
import {PolymorpheusComponent} from '@taiga-ui/polymorpheus';
import {TranslateService} from '@ngx-translate/core';
import {Observable} from 'rxjs';

import {ProviderHelper} from '@gatewee/helpers/helpers/provider.helper';
import {GateweeTableColumn} from '@gatewee/interfaces';
import {PaymentProvider} from '@gatewee/types/provider';
import {IntegrationTableRow} from '@gatewee/types/integration';
import {ProviderPipe} from '@gatewee/helpers/pipes/provider.pipe';
import {IntegrationCapabilitiesPipe} from '@gatewee/utils/pipes/integration-capabilities.pipe';
import {IntegrationDescriptionPipe} from '@gatewee/utils/pipes/integration-description.pipe';
import {
  resolveCredentialComponent,
  resolveProviderFromSlug,
} from './resolve-credential-component';

@Component({
  selector: 'app-gatewee-integrations',
  templateUrl: './gatewee-integrations.component.html',
  styleUrls: ['./gatewee-integrations.component.css'],
  standalone: false,
  providers: [
    DatePipe,
    IntegrationCapabilitiesPipe,
    IntegrationDescriptionPipe,
  ],
})
export class GateweeIntegrationsComponent implements OnInit {
  @Input('id') id!: number;
  @Input() fetchIntegrationsFn!: (id: number) => Observable<unknown>;
  @Input() submitCredentialsFn!: (
    provider: PaymentProvider,
    payload: unknown,
  ) => Observable<void>;

  @Input() enabledProviders!: PaymentProvider[];
  @Input() disableContentPage = false;
  @Input() breadcrumb: {label: string; path?: string}[] = [];
  @Input() title = 'features.integrations.title';
  @Input() titlePath = 'features.integrations.titlePath';

  @ViewChild('newIntegrationModal') newIntegrationModal!: TemplateRef<never>;
  @ViewChild('confirmEditDialog', {read: TemplateRef})
  confirmEditDialog!: TemplateRef<unknown>;

  keys: IntegrationTableRow[] = [];

  columns: GateweeTableColumn[] = [];
  providerForm!: ReturnType<FormBuilder['group']>;

  providers!: {label: string; value: string}[];

  constructor(
    private dialogService: TuiDialogService,
    private alerts: TuiAlertService,
    private fb: FormBuilder,
    private providerHelper: ProviderHelper,
    private providerPipe: ProviderPipe,
    private translate: TranslateService,
    private datePipe: DatePipe,
    private integrationCapabilitiesPipe: IntegrationCapabilitiesPipe,
    private integrationDescriptionPipe: IntegrationDescriptionPipe,
  ) {
    this.providerForm = this.fb.group({
      provider: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.setupColumns();
    this.fetchIntegrations();

    this.providers = this.providerHelper.getSelectItems(
      'payments',
      this.enabledProviders,
    );
  }

  private setupColumns(): void {
    this.columns = [
      {
        label: 'features.integrations.table.columns.id',
        field: 'id',
        sortable: false,
        visible: true,
      },
      {
        label: 'features.integrations.table.columns.provider',
        field: 'integration',
        pipe: {
          fn: this.providerPipe,
          args: [],
        },
        sortable: false,
        visible: true,
        alwaysVisible: true,
      },
      {
        label: 'features.integrations.table.columns.description',
        field: 'description',
        sortable: false,
        visible: true,
        ellipsed: true,
        pipe: {
          fn: this.integrationDescriptionPipe,
          args: [],
        },
      },
      {
        label: 'features.integrations.table.columns.capabilities',
        field: 'capabilities',
        sortable: false,
        visible: true,
        ellipsed: true,
        pipe: {
          fn: this.integrationCapabilitiesPipe,
          args: [],
        },
      },
      {
        label: 'features.integrations.table.columns.createdAt',
        field: 'createdAt',
        sortable: false,
        visible: true,
        pipe: {
          fn: this.datePipe,
          args: ['dd/MM/yyyy HH:mm'],
        },
      },
      {
        label: 'features.integrations.table.columns.updatedAt',
        field: 'updatedAt',
        sortable: false,
        visible: true,
        pipe: {
          fn: this.datePipe,
          args: ['dd/MM/yyyy HH:mm'],
        },
      },
      {
        label: 'features.integrations.table.columns.status',
        field: 'status',
        sortable: false,
        visible: true,
        badge: true,
      },
      {
        label: 'features.integrations.table.columns.actions',
        field: 'actions',
        sortable: false,
        visible: true,
        alwaysVisible: true,
        actions: [
          {
            key: 'edit',
            label: 'features.integrations.actions.configure',
          },
        ],
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  open(
    content: string,
    closeNewIntegrationModal?: (() => void) | {complete?: () => void},
  ) {
    const component = this.getComponentFromContent(content);
    if (!component) {
      return;
    }

    const provider = this.getProviderFromContent(content);

    // Close "select integration" dialog before opening the next dialogs
    // (otherwise it stays open behind the confirmation/credentials dialogs)
    if (typeof closeNewIntegrationModal === 'function') {
      closeNewIntegrationModal();
    } else {
      closeNewIntegrationModal?.complete?.();
    }

    // Open confirmation dialog first
    this.dialogService
      .open<boolean>(this.confirmEditDialog, {
        label: this.translate.instant(
          'features.integrations.confirmEdit.title',
        ),
        size: 's',
        closeable: true,
        dismissible: true,
        data: {id: this.id},
      })
      .subscribe({
        next: (confirmed: boolean) => {
          if (confirmed) {
            // Open credentials dialog
            this.dialogService
              .open(new PolymorpheusComponent(component), {
                label: '',
                size: 'l',
                closeable: true,
                dismissible: true,
                data: {
                  id: this.id,
                  submitCredentialsFn: this.submitCredentialsFn,
                  provider,
                },
              })
              .subscribe({
                complete: () => {
                  // Dialog closed - refresh the list
                  // Success/error handling is done in child component
                  this.fetchIntegrations();
                },
              });
          }
        },
      });
  }

  private getComponentFromContent(content: string): Type<unknown> | null {
    const component = resolveCredentialComponent(content);
    if (!component) {
      this.alerts
        .open('Tipo de integração desconhecido', {
          label: 'Erro',
          appearance: 'error',
        })
        .subscribe();
    }
    return component;
  }

  private getProviderFromContent(content: string): PaymentProvider {
    return resolveProviderFromSlug(content);
  }

  fetchIntegrations() {
    if (!this.fetchIntegrationsFn) {
      console.error('fetchKeysFn is required');
      return;
    }

    this.fetchIntegrationsFn(this.id).subscribe({
      next: data => {
        this.keys = (data as IntegrationTableRow[]) ?? [];
      },
      error: () => {
        this.alerts
          .open(this.translate.instant('features.integrations.fetchError'), {
            label: this.translate.instant('common.status.error'),
            appearance: 'error',
          })
          .subscribe();
      },
    });
  }

  onActionClicked(event: {row: IntegrationTableRow; type: string}) {
    if (event.type === 'edit') {
      this.open(event.row.integration);
    }
  }

  newIntegration() {
    this.dialogService
      .open(this.newIntegrationModal, {
        size: 'm',
        closeable: true,
      })
      .subscribe({
        next: () => {
          // Dialog closed
        },
      });
  }
}
